/**
 * Pagina richiesta prima del login, conservata attraverso il giro OAuth (Google/Microsoft/Apple).
 *
 * Il login con password la passa nello stato del router (Login.tsx: location.state.from, poi
 * /2fa-setup con state.redirectTo); il giro dal provider perde lo stato, quindi prima del redirect
 * la si salva in sessionStorage con una chiave dedicata e /auth/callback la riprende una sola volta.
 *
 * Solo percorsi interni: devono iniziare con un solo "/" (niente "//" o "/\" che il browser tratta
 * come un altro dominio), niente schema, niente caratteri di controllo. Le pagine del login stesso
 * non valgono (si tornerebbe in giro). Cosi' il valore non puo' diventare un redirect aperto.
 */
export const OAUTH_RETURN_PATH_KEY = 'flusia_oauth_return_path';

const MAX_LENGTH = 2048;
const LOGIN_PAGES = /^\/(?:auth\/|(?:login|2fa-setup|register|logout)(?=$|[/?#]))/i;

export function sanitizeReturnPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const path = raw.trim();
  if (!path || path.length > MAX_LENGTH) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(path)) return null;
  if (LOGIN_PAGES.test(path)) return null;
  return path;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** Prima del redirect al provider: salva la pagina richiesta, o toglie un valore vecchio se non ce n'e'. */
export function rememberOAuthReturnPath(path: unknown, storage: StorageLike | null = defaultStorage()): void {
  if (!storage) return;
  try {
    const safe = sanitizeReturnPath(path);
    if (safe) storage.setItem(OAUTH_RETURN_PATH_KEY, safe);
    else storage.removeItem(OAUTH_RETURN_PATH_KEY);
  } catch {
    // storage non disponibile: si atterra sulla pagina predefinita
  }
}

/** In /auth/callback: legge la pagina salvata e la toglie (vale una volta sola). Ricontrolla il valore. */
export function takeOAuthReturnPath(storage: StorageLike | null = defaultStorage()): string | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(OAUTH_RETURN_PATH_KEY);
    storage.removeItem(OAUTH_RETURN_PATH_KEY);
    return sanitizeReturnPath(value);
  } catch {
    return null;
  }
}
